import type { NotesPresentation } from '@/lib/matrix-notes-prefs-store';
import { NOTE_VISIBILITIES, type NoteVisibility, noteVisibilitySchema } from '@mss_creator/shared';
import { z } from 'zod';

/**
 * The settings of the Notes view of a matrix (issue #19, `docs/specs/matrices.md`
 * « Vue Notes »): the status, three list filters, the text search, one sort
 * criterion with its direction, and the presentation. They live in the search
 * params of `?vue=notes`, only what differs from the defaults, and are parsed
 * leniently like `matrix-grid-search.ts`: an unknown value is dropped and its
 * default applies, it never fails the route.
 */

export const NOTES_STATUSES = ['ouvertes', 'resolues', 'toutes'] as const;
export type NotesStatus = (typeof NOTES_STATUSES)[number];
export const NOTES_STATUS_LABELS: Readonly<Record<NotesStatus, string>> = {
  ouvertes: 'Ouvertes',
  resolues: 'Résolues',
  toutes: 'Toutes',
};

/** What the « Cible » filter tells apart: the type of the spot, not the spot itself. */
export const NOTES_TARGET_TYPES = ['matrix', 'cell', 'row', 'column'] as const;
export type NotesTargetType = (typeof NOTES_TARGET_TYPES)[number];
export const NOTES_TARGET_TYPE_LABELS: Readonly<Record<NotesTargetType, string>> = {
  matrix: 'Matrice',
  cell: 'Case',
  row: 'Ligne',
  column: 'Colonne',
};

export const NOTES_SORTS = ['recentes', 'activite', 'grille', 'auteur', 'niveau'] as const;
export type NotesSort = (typeof NOTES_SORTS)[number];
export const NOTES_SORT_LABELS: Readonly<Record<NotesSort, string>> = {
  recentes: 'Plus récentes',
  activite: 'Dernière activité',
  grille: 'Ordre de la grille',
  auteur: 'Auteur',
  niveau: 'Niveau',
};

export type NotesSortDirection = 'asc' | 'desc';

/** The direction a criterion starts in when picked: dates newest first, the others in their natural order. */
export const DEFAULT_NOTES_SORT_DIRECTIONS: Readonly<Record<NotesSort, NotesSortDirection>> = {
  recentes: 'desc',
  activite: 'desc',
  grille: 'asc',
  auteur: 'asc',
  niveau: 'asc',
};

/** Every setting resolved, defaults applied. What the view renders from. */
export interface NotesViewSettings {
  status: NotesStatus;
  levels: NoteVisibility[];
  authors: string[];
  targetTypes: NotesTargetType[];
  q: string;
  sort: NotesSort;
  dir: NotesSortDirection;
  presentation: NotesPresentation;
}

/** The URL shape: only what differs from the defaults (the presentation excepted, see below). */
export interface NotesViewSearch {
  statut?: NotesStatus | undefined;
  niveau?: NoteVisibility[] | undefined;
  auteur?: string[] | undefined;
  cible?: NotesTargetType[] | undefined;
  q?: string | undefined;
  tri?: NotesSort | undefined;
  ordre?: NotesSortDirection | undefined;
  affichage?: NotesPresentation | undefined;
}

/** A multi-value param: known values kept once each, in order; anything else dropped. */
function listOf<T extends string>(itemSchema: z.ZodType<T>) {
  return z
    .unknown()
    .optional()
    .transform((value): T[] => {
      const items: unknown[] = Array.isArray(value) ? value : value === undefined ? [] : [value];
      const kept: T[] = [];
      for (const item of items) {
        const parsed = itemSchema.safeParse(item);
        if (parsed.success && !kept.includes(parsed.data)) kept.push(parsed.data);
      }
      return kept;
    });
}

/**
 * The raw params, leniently: the router JSON-parses search values, so
 * « ?q=2026 » arrives as a number and one author id as a bare string.
 */
export const notesViewSearchShape = {
  statut: z.enum(NOTES_STATUSES).optional().catch(undefined),
  niveau: listOf(noteVisibilitySchema),
  auteur: listOf(z.string().min(1)),
  cible: listOf(z.enum(NOTES_TARGET_TYPES)),
  q: z.union([z.string(), z.number()]).transform(String).optional().catch(undefined),
  tri: z.enum(NOTES_SORTS).optional().catch(undefined),
  ordre: z.enum(['asc', 'desc']).optional().catch(undefined),
  affichage: z.enum(['cartes', 'tableau']).optional().catch(undefined),
};

/**
 * Every setting, from the URL and from the presentation the user stored (the
 * URL wins; without either it is the cards).
 */
export function resolveNotesViewSettings(
  search: NotesViewSearch,
  storedPresentation: NotesPresentation,
): NotesViewSettings {
  const sort = search.tri ?? 'recentes';
  return {
    status: search.statut ?? 'ouvertes',
    levels: search.niveau ?? [],
    authors: search.auteur ?? [],
    targetTypes: search.cible ?? [],
    q: search.q ?? '',
    sort,
    dir: search.ordre ?? DEFAULT_NOTES_SORT_DIRECTIONS[sort],
    presentation: search.affichage ?? storedPresentation,
  };
}

/**
 * The minimal URL form of `settings`: defaults are left out. The presentation
 * is written whenever it differs from the stored one, so a shared link shows
 * what its sender saw.
 */
export function toNotesViewSearch(
  settings: NotesViewSettings,
  storedPresentation: NotesPresentation,
): NotesViewSearch {
  const search: NotesViewSearch = {};
  if (settings.status !== 'ouvertes') search.statut = settings.status;
  if (settings.levels.length > 0) search.niveau = settings.levels;
  if (settings.authors.length > 0) search.auteur = settings.authors;
  if (settings.targetTypes.length > 0) search.cible = settings.targetTypes;
  if (settings.q !== '') search.q = settings.q;
  if (settings.sort !== 'recentes') search.tri = settings.sort;
  if (settings.dir !== DEFAULT_NOTES_SORT_DIRECTIONS[settings.sort]) search.ordre = settings.dir;
  if (settings.presentation !== storedPresentation) search.affichage = settings.presentation;
  return search;
}

/** Orders the levels the way the app words them, whatever the click order. */
export function orderedLevels(levels: readonly NoteVisibility[]): NoteVisibility[] {
  return NOTE_VISIBILITIES.filter((level) => levels.includes(level));
}
